/**
 * RejectMasterFileDialog — admin-only modal to reject a master file.
 * Required comment field; on confirm calls the backend, which marks the
 * file rejected, sends the customer email, and pushes a history entry.
 */
import { useState } from 'react';
import { XCircle } from 'lucide-react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { ordersApi } from '../lib/api';

export function RejectMasterFileDialog({
    open,
    onOpenChange,
    orderId,
    file,
    onRejected,
}) {
    const [comment, setComment] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const close = () => {
        if (submitting) return;
        setComment('');
        onOpenChange(false);
    };

    const submit = async () => {
        const trimmed = comment.trim();
        if (!trimmed) {
            toast.error('Please add a reason — the customer will see this.');
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await ordersApi.rejectMasterFile(orderId, file.id, trimmed);
            toast.success('Master file rejected — customer notified by email.');
            onRejected?.(data);
            setComment('');
            onOpenChange(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to reject file');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={close}>
            <DialogContent className="max-w-md" data-testid="reject-master-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-rose-700">
                        <XCircle className="h-5 w-5" />
                        Reject master file
                    </DialogTitle>
                    <DialogDescription className="text-xs">
                        The customer will receive an email asking them to upload a replacement.
                        The file stays in history.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-2 py-2">
                    <div className="text-xs text-slate-500">File</div>
                    <div className="font-mono text-sm bg-slate-50 border rounded px-2 py-1 truncate" title={file?.original_filename}>
                        {file?.original_filename}
                    </div>
                </div>
                <div className="space-y-1.5">
                    <label className="text-xs font-medium" htmlFor="reject-master-comment">
                        Reason / what's needed <span className="text-rose-500">*</span>
                    </label>
                    <Textarea
                        id="reject-master-comment"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        rows={4}
                        placeholder="E.g. Audio appears corrupted around 02:34. Please re-master Side A and re-upload."
                        disabled={submitting}
                        data-testid="reject-master-comment-input"
                        autoFocus
                    />
                </div>

                <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={close} disabled={submitting} data-testid="reject-master-cancel">
                        Cancel
                    </Button>
                    <Button
                        className="bg-rose-600 hover:bg-rose-700"
                        onClick={submit}
                        disabled={submitting || !comment.trim()}
                        data-testid="reject-master-confirm"
                    >
                        {submitting ? 'Sending…' : 'Request replacement'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default RejectMasterFileDialog;
